import { lazy, Suspense } from 'react'
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router'
import { RootLayout } from '@/components/layout/root-layout'
import { RouteError } from '@/components/layout/route-error'
import { Loading } from '@/components/ui/states'
import { HouseholdLayout } from '@/features/households/household-layout'
import { NotFoundPage } from '@/features/not-found'
import { PublicOnly, RequireAuth } from './guards'

/** Route modules are split into their own chunks (React Router `lazy`). */
const page = <T extends string>(load: () => Promise<Record<T, React.ComponentType>>, name: T) => ({
  lazy: async () => ({ Component: (await load())[name] }),
})

const catalogEnabled = import.meta.env.DEV || import.meta.env.VITE_ENABLE_CATALOG === 'true'

const CatalogPage = catalogEnabled ? lazy(() => import('@/routes/catalog')) : null

export const routes: RouteObject[] = [
  {
    element: <RootLayout />,
    errorElement: <RouteError />,
    HydrateFallback: Loading,
    children: [
      {
        element: <PublicOnly />,
        children: [
          { path: 'login', ...page(() => import('@/features/auth/login'), 'LoginPage') },
          { path: 'register', ...page(() => import('@/features/auth/register'), 'RegisterPage') },
        ],
      },
      { path: 'forgot-password', ...page(() => import('@/features/auth/forgot-password'), 'ForgotPasswordPage') },
      { path: 'reset-password', ...page(() => import('@/features/auth/reset-password'), 'ResetPasswordPage') },
      { path: 'verify-email', ...page(() => import('@/features/auth/verify-email'), 'VerifyEmailPage') },
      { path: 't/:token', ...page(() => import('@/features/technician/technician-page'), 'TechnicianPage') },
      { path: 'install', ...page(() => import('@/pwa/install-page'), 'InstallPage') },
      { path: 'privacy', ...page(() => import('@/features/legal/legal-page'), 'PrivacyPage') },
      { path: 'terms', ...page(() => import('@/features/legal/legal-page'), 'TermsPage') },
      { path: 'goodbye', ...page(() => import('@/features/settings/delete-account'), 'GoodbyePage') },
      { path: 'invite/:token', ...page(() => import('@/features/invitations/invite-page'), 'InvitePage') },
      {
        element: <RequireAuth />,
        children: [
          { index: true, ...page(() => import('@/features/households/home-redirect'), 'HomeRedirect') },
          { path: 'households/new', ...page(() => import('@/features/households/new-household'), 'NewHouseholdPage') },
          { path: 'notifications', ...page(() => import('@/features/notifications/notification-center'), 'NotificationCenterPage') },
          { path: 'settings', ...page(() => import('@/features/settings/settings-page'), 'SettingsPage') },
          { path: 'confirm-email-change', ...page(() => import('@/features/settings/confirm-email-change'), 'ConfirmEmailChangePage') },
          {
            path: 'h/:householdId',
            element: <HouseholdLayout />,
            errorElement: <RouteError />,
            children: [
              { index: true, ...page(() => import('@/features/households/household-home'), 'HouseholdHomePage') },
              { path: 'house', ...page(() => import('@/features/households/house'), 'HousePage') },
              { path: 'export', ...page(() => import('@/features/households/export-page'), 'ExportPage') },
              { path: 'reminders', ...page(() => import('@/features/reminders/reminder-list'), 'ReminderListPage') },
              { path: 'reminders/new', ...page(() => import('@/features/reminders/reminder-form'), 'NewReminderPage') },
              { path: 'reminders/:reminderId/edit', ...page(() => import('@/features/reminders/reminder-form'), 'EditReminderPage') },
              { path: 'house/documents', ...page(() => import('@/features/documents/document-library'), 'DocumentLibraryPage') },
              { path: 'assets', ...page(() => import('@/features/assets/asset-list'), 'AssetListPage') },
              { path: 'assets/new', ...page(() => import('@/features/assets/asset-form-routes'), 'NewAssetPage') },
              { path: 'assets/from-photo', ...page(() => import('@/features/extraction/routes'), 'FromPhotoPage') },
              { path: 'assets/:assetId', ...page(() => import('@/features/assets/asset-detail'), 'AssetDetailPage') },
              { path: 'assets/:assetId/edit', ...page(() => import('@/features/assets/asset-form-routes'), 'EditAssetPage') },
              { path: 'assets/:assetId/read', ...page(() => import('@/features/extraction/routes'), 'ReadDocumentPage') },
              { path: 'requests', ...page(() => import('@/features/requests/request-list'), 'RequestListPage') },
              { path: 'requests/new', ...page(() => import('@/features/requests/new-request'), 'NewRequestPage') },
              { path: 'requests/:requestId', ...page(() => import('@/features/requests/request-detail'), 'RequestDetailPage') },
              { path: 'requests/:requestId/share', ...page(() => import('@/features/requests/new-request'), 'RequestShareRoute') },
              { path: 'requests/:requestId/quotes', ...page(() => import('@/features/requests/quotes-page'), 'QuotesPage') },
              { path: 'services/new', ...page(() => import('@/features/maintenance/routes'), 'NewRecordPage') },
              { path: 'services/:recordId', ...page(() => import('@/features/maintenance/record-detail'), 'RecordDetailPage') },
              { path: 'services/:recordId/edit', ...page(() => import('@/features/maintenance/routes'), 'EditRecordPage') },
              { path: '*', element: <NotFoundPage inShell /> },
            ],
          },
        ],
      },
      ...(CatalogPage
        ? [
            {
              path: '_catalog',
              element: (
                <Suspense fallback={<Loading />}>
                  <CatalogPage />
                </Suspense>
              ),
            } satisfies RouteObject,
          ]
        : []),
      { path: 'home', element: <Navigate to="/" replace /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]

export function createRouter() {
  return createBrowserRouter(routes)
}
